import { useEffect } from "react";
import { prefetchPage, type PageImport } from "@/lib/lazyPages";

/**
 * Prefetches a list of lazy chunks during browser idle time.
 * Skips entirely on save-data or slow connections.
 */
export function useIdlePrefetch(imports: PageImport[], delayMs = 1500) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const conn = (navigator as any).connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && ["slow-2g", "2g"].includes(conn.effectiveType)) return;

    const ric: ((cb: () => void) => number) =
      (window as any).requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, delayMs));
    const cic: ((id: number) => void) =
      (window as any).cancelIdleCallback ?? ((id: number) => window.clearTimeout(id));

    const handles: number[] = [];
    imports.forEach((fn, i) => {
      handles.push(ric(() => prefetchPage(fn)));
      // Stagger slightly so we don't saturate the network at once.
      void i;
    });
    return () => handles.forEach((h) => cic(h));
  }, [imports, delayMs]);
}
